import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { cobrettisAPI, cuentasEmailAPI } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import WorkflowVisual from '../components/WorkflowVisual';
import { 
  ArrowLeft, Plus, Phone, Mail, FileText, Euro, Clock, 
  CheckCircle2, XCircle, AlertTriangle, Calendar, User,
  Send, Trash2, RefreshCw, DollarSign, CreditCard, Edit,
  Download, FileDown, Filter, ChevronDown, ChevronUp, ExternalLink,
  MessageCircle, Paperclip, Upload, Eye, X, CheckSquare,
  Scale, ArrowRight
} from 'lucide-react';

// Constantes
const ESTADO_COLORS = {
  PENDIENTE: 'bg-gray-100 text-gray-800',
  EN_RECLAMACION: 'bg-orange-100 text-orange-800',
  EXTRAJUDICIAL: 'bg-red-100 text-red-800',
  DEMANDA_EJECUCION: 'bg-red-200 text-red-900',
  COBRADO: 'bg-green-100 text-green-800',
  FINALIZADO: 'bg-blue-100 text-blue-800',
  CANCELADO: 'bg-gray-200 text-gray-600',
};

const TIPO_CONTACTO_ICONS = {
  EMAIL: Mail,
  TELEFONO: Phone,
  WHATSAPP: MessageCircle,
  BUROFAX: FileText,
  CARTA: FileText,
  PRESENCIAL: User,
  OTRO: FileText
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric'
  });
};

const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

export default function CobrettiDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Estado principal
  const [cobretti, setCobretti] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rondas, setRondas] = useState([]);
  const [contactos, setContactos] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [planPagos, setPlanPagos] = useState([]);
  const [timeline, setTimeline] = useState([]);
  
  // Modales
  const [showContactoModal, setShowContactoModal] = useState(false);
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [showEstadoModal, setShowEstadoModal] = useState(false);
  const [showPlanPagosModal, setShowPlanPagosModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [showTramitarModal, setShowTramitarModal] = useState(false);
  const [showDetalleContactoModal, setShowDetalleContactoModal] = useState(false);
  const [contactoDetalle, setContactoDetalle] = useState(null);
  
  // Archivos adjuntos email
  const [emailAdjuntos, setEmailAdjuntos] = useState([]);
  const emailFileInputRef = useRef(null);
  
  // Archivos adjuntos tramitar
  const [tramitarAdjuntos, setTramitarAdjuntos] = useState([]);
  const tramitarFileInputRef = useRef(null);
  
  // Configuración
  const [estados, setEstados] = useState([]);
  const [plantillas, setPlantillas] = useState([]);
  const [cuentasEmail, setCuentasEmail] = useState([]);
  const [tiposDeuda, setTiposDeuda] = useState([]);
  
  // Modal editar
  const [showEditarModal, setShowEditarModal] = useState(false);
  const [editarForm, setEditarForm] = useState({
    tipo_deuda: '',
    concepto: '',
    importe_principal: '',
    importe_intereses: '',
    notas: ''
  });
  
  // Formulario contacto
  const [contactoForm, setContactoForm] = useState({
    tipo_contacto: 'TELEFONO',
    asunto: '',
    contenido: '',
    resultado: 'ENVIADO',
    respuesta_deudor: ''
  });
  
  // Formulario pago
  const [pagoForm, setPagoForm] = useState({
    fecha_pago: new Date().toISOString().split('T')[0],
    importe: '',
    metodo_pago: 'TRANSFERENCIA',
    referencia: '',
    notas: ''
  });
  
  // Formulario cambio estado
  const [estadoForm, setEstadoForm] = useState({
    nuevo_estado: '',
    motivo_cancelacion: ''
  });
  
  // Formulario email
  const [emailForm, setEmailForm] = useState({
    plantilla_id: '',
    cuenta_smtp_id: '',
    asunto: '',
    contenido: '',
    destinatario_email: ''
  });
  
  // Formulario plan de pagos automático
  const [planAutoForm, setPlanAutoForm] = useState({
    num_cuotas: 3,
    fecha_primera_cuota: new Date(Date.now() + 30*24*60*60*1000).toISOString().split('T')[0]
  });
  
  // Formulario WhatsApp
  const [whatsAppForm, setWhatsAppForm] = useState({
    contenido: '',
    resultado: 'ENVIADO',
    respuesta_deudor: ''
  });
  
  // Formulario Tramitar Fase
  const [tramitarForm, setTramitarForm] = useState({
    descripcion: '',
    resultado: 'COMPLETADO'
  });

  // ==========================================
  // DEMANDA DE EJECUCIÓN - Estados y Formulario
  // ==========================================
  const [showDemandaModal, setShowDemandaModal] = useState(false);
  const [demandaLoading, setDemandaLoading] = useState(false);
  const [datosDemanda, setDatosDemanda] = useState(null);
  const [demandaAdjuntos, setDemandaAdjuntos] = useState([]);
  const demandaFileInputRef = useRef(null);
  const [demandaForm, setDemandaForm] = useState({
    resultado: 'EN_PROCESO', // EN_PROCESO, PENDIENTE_RESPUESTA, COMPLETADO
    descripcion: '',
    // Campos para cuando resultado = COMPLETADO
    cuenta_smtp_id: '',
    procurador_id: '',
    usar_otro_procurador: false,
    nuevo_procurador_id: '',
    asunto_email: '',
    contenido_email: '',
    enviar_email: true
  });
  // Estado para el paso del flujo de finalización
  const [demandaCompletada, setDemandaCompletada] = useState(false);
  const [expedienteDemandaCreado, setExpedienteDemandaCreado] = useState(null);

  // Cargar datos
  useEffect(() => {
    if (id) {
      loadAll();
    }
  }, [id]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cobrettiRes, rondasRes, contactosRes, pagosRes, planRes, timelineRes, estadosRes, plantillasRes, cuentasRes, tiposRes] = await Promise.all([
        cobrettisAPI.getById(id),
        cobrettisAPI.getRondas(id),
        cobrettisAPI.getContactos(id),
        cobrettisAPI.getPagos(id),
        cobrettisAPI.getPlanPagos(id),
        cobrettisAPI.getTimeline(id),
        cobrettisAPI.getEstados(),
        cobrettisAPI.getPlantillas({ activa: true }),
        cuentasEmailAPI.getAll(),
        cobrettisAPI.getTiposDeuda()
      ]);
      
      setCobretti(cobrettiRes.data);
      setRondas(rondasRes.data || []);
      setContactos(contactosRes.data || []);
      setPagos(pagosRes.data || []);
      setPlanPagos(planRes.data || []);
      setTimeline(timelineRes.data || []);
      setEstados(estadosRes.data.estados || []);
      setPlantillas(plantillasRes.data || []);
      setCuentasEmail(cuentasRes.data?.items || cuentasRes.data || []);
      setTiposDeuda(tiposRes.data?.tipos || []);
      
      // Inicializar email del deudor
      if (cobrettiRes.data?.contacto_deudor_email) {
        setEmailForm(prev => ({ ...prev, destinatario_email: cobrettiRes.data.contacto_deudor_email }));
      }
    } catch (error) {
      console.error('Error cargando datos:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar el registro',
        variant: 'destructive'
      });
      navigate('/cobrettis');
    } finally {
      setLoading(false);
    }
  };

  // Registrar contacto
  const handleRegistrarContacto = async () => {
    if (!contactoForm.contenido) {
      toast({ title: 'Error', description: 'Ingrese el contenido del contacto', variant: 'destructive' });
      return;
    }
    
    try {
      const response = await cobrettisAPI.crearContacto(id, contactoForm);
      toast({ title: 'Éxito', description: 'Contacto registrado correctamente' });
      setShowContactoModal(false);
      setContactoForm({
        tipo_contacto: 'TELEFONO',
        asunto: '',
        contenido: '',
        resultado: 'ENVIADO',
        respuesta_deudor: ''
      });
      loadAll();
      
      // Mostrar alerta si se agotó la ronda
      if (response.data.alerta_ronda) {
        toast({
          title: 'Atención',
          description: response.data.alerta_ronda,
          variant: 'warning'
        });
      }
    } catch (error) {
      console.error('Error registrando contacto:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo registrar el contacto',
        variant: 'destructive'
      });
    }
  };

  // Registrar pago
  const handleRegistrarPago = async () => {
    if (!pagoForm.importe || parseFloat(pagoForm.importe) <= 0) {
      toast({ title: 'Error', description: 'Ingrese un importe válido', variant: 'destructive' });
      return;
    }
    
    try {
      const data = {
        ...pagoForm,
        fecha_pago: new Date(pagoForm.fecha_pago).toISOString(),
        importe: parseFloat(pagoForm.importe)
      };
      
      const response = await cobrettisAPI.crearPago(id, data);
      toast({ title: 'Éxito', description: 'Pago registrado correctamente' });
      setShowPagoModal(false);
      setPagoForm({
        fecha_pago: new Date().toISOString().split('T')[0],
        importe: '',
        metodo_pago: 'TRANSFERENCIA',
        referencia: '',
        notas: ''
      });
      loadAll();
      
      // Mostrar mensaje si cobró todo
      if (response.data.cobrado_completo) {
        toast({
          title: '¡Deuda Cobrada!',
          description: 'La deuda ha sido cobrada completamente',
          variant: 'success'
        });
      }
    } catch (error) {
      console.error('Error registrando pago:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo registrar el pago',
        variant: 'destructive'
      });
    }
  };

  // Cambiar estado
  const handleCambiarEstado = async () => {
    if (!estadoForm.nuevo_estado) {
      toast({ title: 'Error', description: 'Seleccione un estado', variant: 'destructive' });
      return;
    }
    
    if (estadoForm.nuevo_estado === 'CANCELADO' && !estadoForm.motivo_cancelacion) {
      toast({ title: 'Error', description: 'Ingrese el motivo de cancelación', variant: 'destructive' });
      return;
    }
    
    try {
      await cobrettisAPI.cambiarEstado(id, estadoForm);
      toast({ title: 'Éxito', description: 'Estado actualizado correctamente' });
      setShowEstadoModal(false);
      setEstadoForm({ nuevo_estado: '', motivo_cancelacion: '' });
      loadAll();
    } catch (error) {
      console.error('Error cambiando estado:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo cambiar el estado',
        variant: 'destructive'
      });
    }
  };

  // Nueva ronda
  const handleNuevaRonda = async () => {
    try {
      await cobrettisAPI.crearRonda(id, {});
      toast({ title: 'Éxito', description: 'Nueva ronda creada' });
      loadAll();
    } catch (error) {
      console.error('Error creando ronda:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo crear la ronda',
        variant: 'destructive'
      });
    }
  };

  // Crear actuación
  const handleCrearActuacion = async () => {
    try {
      await cobrettisAPI.crearActuacion(id);
      toast({ title: 'Éxito', description: 'Actuación creada en el expediente' });
      loadAll();
    } catch (error) {
      console.error('Error creando actuación:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo crear la actuación',
        variant: 'destructive'
      });
    }
  };

  // Eliminar pago
  const handleEliminarPago = async (pagoId) => {
    if (!window.confirm('¿Está seguro de eliminar este pago?')) return;
    
    try {
      await cobrettisAPI.eliminarPago(id, pagoId);
      toast({ title: 'Éxito', description: 'Pago eliminado' });
      loadAll();
    } catch (error) {
      console.error('Error eliminando pago:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo eliminar el pago',
        variant: 'destructive'
      });
    }
  };

  // Cargar plantilla renderizada
  const handleCargarPlantilla = async (plantillaId) => {
    if (!plantillaId) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/renderizar-plantilla/${plantillaId}`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      setEmailForm(prev => ({
        ...prev,
        plantilla_id: plantillaId,
        asunto: data.asunto,
        contenido: data.cuerpo,
        destinatario_email: data.destinatario || cobretti?.contacto_deudor_email || ''
      }));
      
      toast({ title: 'Plantilla cargada', description: data.plantilla_nombre });
    } catch (error) {
      console.error('Error cargando plantilla:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cargar la plantilla',
        variant: 'destructive'
      });
    }
  };

  // Enviar email
  const handleEnviarEmail = async () => {
    if (!emailForm.cuenta_smtp_id) {
      toast({ title: 'Error', description: 'Seleccione una cuenta de email', variant: 'destructive' });
      return;
    }
    if (!emailForm.destinatario_email) {
      toast({ title: 'Error', description: 'Ingrese el email del destinatario', variant: 'destructive' });
      return;
    }
    if (!emailForm.asunto) {
      toast({ title: 'Error', description: 'Ingrese el asunto del email', variant: 'destructive' });
      return;
    }
    if (!emailForm.contenido) {
      toast({ title: 'Error', description: 'Ingrese el contenido del email', variant: 'destructive' });
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/enviar-email`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plantilla_id: emailForm.plantilla_id ? parseInt(emailForm.plantilla_id) : null,
          cuenta_smtp_id: parseInt(emailForm.cuenta_smtp_id),
          asunto: emailForm.asunto,
          contenido: emailForm.contenido,
          destinatario_email: emailForm.destinatario_email
        })
      });
      
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Error parsing response:', responseText);
        throw new Error('Error en la respuesta del servidor');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error enviando email');
      }
      
      toast({ title: 'Éxito', description: 'Email registrado correctamente' });
      setShowEmailModal(false);
      setEmailForm({
        plantilla_id: '',
        cuenta_smtp_id: '',
        asunto: '',
        contenido: '',
        destinatario_email: cobretti?.contacto_deudor_email || ''
      });
      setEmailAdjuntos([]);
      loadAll();
      
      if (data.alerta_ronda) {
        toast({ title: 'Atención', description: data.alerta_ronda, variant: 'warning' });
      }
    } catch (error) {
      console.error('Error enviando email:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo enviar el email',
        variant: 'destructive'
      });
    }
  };
  
  // Registrar WhatsApp
  const handleRegistrarWhatsApp = async () => {
    if (!whatsAppForm.contenido) {
      toast({ title: 'Error', description: 'Describa los mensajes enviados por WhatsApp', variant: 'destructive' });
      return;
    }
    
    try {
      const data = {
        tipo_contacto: 'WHATSAPP',
        asunto: 'Contacto por WhatsApp',
        contenido: whatsAppForm.contenido,
        resultado: whatsAppForm.resultado,
        respuesta_deudor: whatsAppForm.respuesta_deudor
      };
      
      const response = await cobrettisAPI.crearContacto(id, data);
      toast({ title: 'Éxito', description: 'Contacto WhatsApp registrado correctamente' });
      setShowWhatsAppModal(false);
      setWhatsAppForm({
        contenido: '',
        resultado: 'ENVIADO',
        respuesta_deudor: ''
      });
      loadAll();
      
      if (response.data.alerta_ronda) {
        toast({ title: 'Atención', description: response.data.alerta_ronda, variant: 'warning' });
      }
    } catch (error) {
      console.error('Error registrando WhatsApp:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo registrar el contacto',
        variant: 'destructive'
      });
    }
  };
  
  // Tramitar fase (Extrajudicial/Demanda)
  const handleTramitarFase = async () => {
    if (!tramitarForm.descripcion) {
      toast({ title: 'Error', description: 'Describa las acciones realizadas', variant: 'destructive' });
      return;
    }
    
    try {
      // Usar FormData para enviar archivos
      const formData = new FormData();
      formData.append('descripcion', tramitarForm.descripcion);
      formData.append('resultado', tramitarForm.resultado);
      formData.append('tipo_contacto', cobretti.estado === 'EXTRAJUDICIAL' ? 'BUROFAX' : 'OTRO');
      
      // Añadir archivos adjuntos
      tramitarAdjuntos.forEach((file) => {
        formData.append('adjuntos', file);
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/tramitar-fase`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}`
        },
        body: formData
      });
      
      // Leer el texto primero para manejar errores
      const responseText = await response.text();
      let data;
      
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        console.error('Error parsing response:', responseText);
        throw new Error('Error en la respuesta del servidor');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error tramitando fase');
      }
      
      toast({ title: 'Éxito', description: 'Fase tramitada correctamente' });
      setShowTramitarModal(false);
      setTramitarForm({ descripcion: '', resultado: 'COMPLETADO' });
      setTramitarAdjuntos([]);
      loadAll();
      
      // Si hay siguiente estado, preguntar si quiere avanzar
      if (data.siguiente_estado) {
        if (window.confirm(`¿Desea avanzar al estado "${data.siguiente_estado_nombre}"?`)) {
          await cobrettisAPI.cambiarEstado(id, { nuevo_estado: data.siguiente_estado });
          loadAll();
        }
      }
    } catch (error) {
      console.error('Error tramitando fase:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo tramitar la fase',
        variant: 'destructive'
      });
    }
  };
  
  // Manejadores de archivos adjuntos
  const handleEmailFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setEmailAdjuntos(prev => [...prev, ...files]);
  };
  
  const handleTramitarFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setTramitarAdjuntos(prev => [...prev, ...files]);
  };
  
  const removeEmailAdjunto = (index) => {
    setEmailAdjuntos(prev => prev.filter((_, i) => i !== index));
  };
  
  const removeTramitarAdjunto = (index) => {
    setTramitarAdjuntos(prev => prev.filter((_, i) => i !== index));
  };

  // ==========================================
  // FUNCIONES DEMANDA DE EJECUCIÓN
  // ==========================================
  
  // Cargar datos de demanda cuando se abre el modal
  const cargarDatosDemanda = async () => {
    setDemandaLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/datos-demanda`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error cargando datos');
      }
      
      const data = await response.json();
      setDatosDemanda(data);
      
      // Pre-llenar el formulario con datos sugeridos
      setDemandaForm(prev => ({
        ...prev,
        procurador_id: data.procurador?.id || '',
        asunto_email: data.email_sugerido?.asunto || '',
        contenido_email: data.email_sugerido?.contenido || ''
      }));
      
    } catch (error) {
      console.error('Error cargando datos demanda:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo cargar los datos de demanda',
        variant: 'destructive'
      });
    } finally {
      setDemandaLoading(false);
    }
  };
  
  // Abrir modal de demanda
  const handleAbrirDemanda = () => {
    setShowDemandaModal(true);
    setDemandaForm({
      resultado: 'EN_PROCESO',
      descripcion: '',
      cuenta_smtp_id: '',
      procurador_id: '',
      usar_otro_procurador: false,
      nuevo_procurador_id: '',
      asunto_email: '',
      contenido_email: '',
      enviar_email: true
    });
    setDemandaAdjuntos([]);
    setDemandaCompletada(false);
    setExpedienteDemandaCreado(null);
    cargarDatosDemanda();
  };
  
  // Manejar archivos adjuntos demanda
  const handleDemandaFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setDemandaAdjuntos(prev => [...prev, ...files]);
  };
  
  const removeDemandaAdjunto = (index) => {
    setDemandaAdjuntos(prev => prev.filter((_, i) => i !== index));
  };
  
  // Guardar progreso de demanda (EN_PROCESO o PENDIENTE_RESPUESTA)
  const handleGuardarProgresoDemanda = async () => {
    if (!demandaForm.descripcion) {
      toast({ title: 'Error', description: 'Describa las acciones realizadas', variant: 'destructive' });
      return;
    }
    
    try {
      // Usar el endpoint existente de tramitar-fase para guardar progreso
      const formData = new FormData();
      formData.append('descripcion', demandaForm.descripcion);
      formData.append('resultado', demandaForm.resultado);
      formData.append('tipo_contacto', 'OTRO');
      
      demandaAdjuntos.forEach((file) => {
        formData.append('adjuntos', file);
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/tramitar-fase`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` },
        body: formData
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error guardando progreso');
      
      toast({ title: 'Progreso guardado', description: `Estado: ${demandaForm.resultado}` });
      setShowDemandaModal(false);
      loadAll();
    } catch (error) {
      console.error('Error guardando progreso:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo guardar el progreso',
        variant: 'destructive'
      });
    }
  };
  
  // Completar demanda - Crear expediente y enviar email
  const handleCompletarDemanda = async () => {
    // Validaciones
    if (!demandaForm.cuenta_smtp_id) {
      toast({ title: 'Error', description: 'Seleccione una cuenta de email', variant: 'destructive' });
      return;
    }
    
    const procuradorFinal = demandaForm.usar_otro_procurador ? demandaForm.nuevo_procurador_id : demandaForm.procurador_id;
    if (!procuradorFinal) {
      toast({ title: 'Error', description: 'Seleccione un procurador', variant: 'destructive' });
      return;
    }
    
    if (!demandaForm.asunto_email) {
      toast({ title: 'Error', description: 'El asunto del email es obligatorio', variant: 'destructive' });
      return;
    }
    
    if (!demandaForm.contenido_email) {
      toast({ title: 'Error', description: 'El contenido del email es obligatorio', variant: 'destructive' });
      return;
    }
    
    setDemandaLoading(true);
    try {
      const formData = new FormData();
      formData.append('cuenta_smtp_id', demandaForm.cuenta_smtp_id);
      formData.append('procurador_id', demandaForm.procurador_id || procuradorFinal);
      formData.append('usar_otro_procurador', demandaForm.usar_otro_procurador);
      if (demandaForm.usar_otro_procurador && demandaForm.nuevo_procurador_id) {
        formData.append('nuevo_procurador_id', demandaForm.nuevo_procurador_id);
      }
      formData.append('asunto_email', demandaForm.asunto_email);
      formData.append('contenido_email', demandaForm.contenido_email);
      formData.append('enviar_email', demandaForm.enviar_email);
      
      demandaAdjuntos.forEach((file) => {
        formData.append('adjuntos', file);
      });
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/completar-demanda`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` },
        body: formData
      });
      
      // Leer el body solo una vez
      const responseText = await response.text();
      let data;
      try {
        data = JSON.parse(responseText);
      } catch (e) {
        throw new Error(responseText || 'Error en la respuesta del servidor');
      }
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error completando demanda');
      }
      
      setExpedienteDemandaCreado(data.nuevo_expediente);
      setDemandaCompletada(true);
      
      toast({ 
        title: '¡Demanda tramitada!', 
        description: `Expediente ${data.nuevo_expediente.numero} creado. ${data.email_enviado ? 'Email enviado al procurador.' : 'Email pendiente de envío.'}` 
      });
      
      loadAll();
    } catch (error) {
      console.error('Error completando demanda:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo completar la demanda',
        variant: 'destructive'
      });
    } finally {
      setDemandaLoading(false);
    }
  };
  
  // Confirmar finalización y avanzar a COBRADO
  const handleConfirmarFinalizacion = async () => {
    try {
      await cobrettisAPI.cambiarEstado(id, { nuevo_estado: 'COBRADO' });
      toast({ title: 'Estado actualizado', description: 'El caso ha avanzado a la fase de Cobrado' });
      setShowDemandaModal(false);
      loadAll();
    } catch (error) {
      console.error('Error finalizando:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo cambiar el estado',
        variant: 'destructive'
      });
    }
  };
  
  // Registrar pago final (desde el estado COBRADO)
  const handleRegistrarPagoFinal = async (pagoData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/finalizar-cobrado`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` },
        body: new URLSearchParams(pagoData)
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || 'Error registrando pago');
      
      toast({ 
        title: data.cobrado_completo ? '¡Deuda cobrada completamente!' : 'Pago registrado',
        description: `Saldo pendiente: ${formatCurrency(data.saldo_pendiente)}`
      });
      
      loadAll();
      return data;
    } catch (error) {
      console.error('Error registrando pago final:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo registrar el pago',
        variant: 'destructive'
      });
      throw error;
    }
  };
  
  // Finalizar expediente COBRETTIS cuando la deuda está completamente cobrada
  const handleFinalizarExpediente = async () => {
    try {
      await cobrettisAPI.cambiarEstado(id, { nuevo_estado: 'FINALIZADO' });
      toast({ 
        title: '¡Expediente Finalizado!', 
        description: 'El proceso de cobro ha concluido satisfactoriamente' 
      });
      loadAll();
    } catch (error) {
      console.error('Error finalizando expediente:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo finalizar el expediente',
        variant: 'destructive'
      });
    }
  };
  
  // Ver detalle de contacto (email enviado)
  const handleVerDetalleContacto = (contacto) => {
    setContactoDetalle(contacto);
    setShowDetalleContactoModal(true);
  };
  // Descarga segura de adjuntos con autenticación
  const handleDescargarAdjunto = async (adj) => {
    try {
      const downloadUrl = adj.url 
        ? (adj.url.startsWith('http') ? adj.url : `${process.env.REACT_APP_BACKEND_URL}${adj.url}`)
        : adj.nombre_almacenado 
          ? `${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/adjuntos/${adj.nombre_almacenado}`
          : null;
      
      if (!downloadUrl) {
        toast({ title: 'Error', description: 'Archivo no disponible', variant: 'destructive' });
        return;
      }

      const response = await fetch(downloadUrl, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` }
      });
      
      if (!response.ok) {
        throw new Error('Error al descargar');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = adj.filename || adj.nombre || 'archivo';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error descargando:', error);
      toast({ title: 'Error', description: 'No se pudo descargar el archivo', variant: 'destructive' });
    }
  };


  // Eliminar contacto del timeline
  const handleEliminarContacto = async (contactoId) => {
    if (!window.confirm('¿Estás seguro de eliminar este registro del timeline?')) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/contactos/${contactoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Error del servidor');
      }
      
      toast({ title: 'Éxito', description: 'Registro eliminado correctamente' });
      loadAll();
    } catch (error) {
      console.error('Error eliminando contacto:', error);
      toast({ title: 'Error', description: error.message || 'No se pudo eliminar el registro', variant: 'destructive' });
    }
  };

  // Generar plan de pagos automático
  const handleGenerarPlanPagos = async () => {
    if (planAutoForm.num_cuotas < 1 || planAutoForm.num_cuotas > 60) {
      toast({ title: 'Error', description: 'El número de cuotas debe estar entre 1 y 60', variant: 'destructive' });
      return;
    }
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/generar-plan-pagos`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          num_cuotas: parseInt(planAutoForm.num_cuotas),
          fecha_primera_cuota: planAutoForm.fecha_primera_cuota
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error generando plan de pagos');
      }
      
      toast({ 
        title: 'Plan de Pagos Creado', 
        description: `${data.cuotas.length} cuotas de ${formatCurrency(data.cuota_mensual)}` 
      });
      setShowPlanPagosModal(false);
      loadAll();
    } catch (error) {
      console.error('Error generando plan de pagos:', error);
      toast({
        title: 'Error',
        description: error.message || 'No se pudo generar el plan de pagos',
        variant: 'destructive'
      });
    }
  };

  // Abrir modal de edición
  const handleAbrirEditar = () => {
    setEditarForm({
      tipo_deuda: cobretti.tipo_deuda || 'COSTAS',
      concepto: cobretti.concepto || '',
      importe_principal: cobretti.importe_principal?.toString() || '',
      importe_intereses: cobretti.importe_intereses?.toString() || '0',
      notas: cobretti.notas || ''
    });
    setShowEditarModal(true);
  };

  // Guardar edición
  const handleGuardarEdicion = async () => {
    if (!editarForm.concepto) {
      toast({ title: 'Error', description: 'El concepto es obligatorio', variant: 'destructive' });
      return;
    }
    if (!editarForm.importe_principal || parseFloat(editarForm.importe_principal) <= 0) {
      toast({ title: 'Error', description: 'El importe principal debe ser mayor a 0', variant: 'destructive' });
      return;
    }

    try {
      await cobrettisAPI.update(id, {
        tipo_deuda: editarForm.tipo_deuda,
        concepto: editarForm.concepto,
        importe_principal: parseFloat(editarForm.importe_principal),
        importe_intereses: parseFloat(editarForm.importe_intereses || 0),
        notas: editarForm.notas || null
      });

      toast({ title: 'Éxito', description: 'Registro actualizado correctamente' });
      setShowEditarModal(false);
      loadAll();
    } catch (error) {
      console.error('Error actualizando:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'No se pudo actualizar el registro',
        variant: 'destructive'
      });
    }
  };

  // Exportar PDF
  const handleExportPDF = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/cobrettis/${id}/export-pdf`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('lexduo_token')}` }
      });
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Error al exportar');
      }
      
      // Crear blob y descargar
      const blob = new Blob([data.html_content], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({ title: 'Exportado', description: 'Archivo descargado correctamente' });
    } catch (error) {
      console.error('Error exportando PDF:', error);
      toast({
        title: 'Error',
        description: 'No se pudo exportar el documento',
        variant: 'destructive'
      });
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!cobretti) {
    return (
      <div className="p-6 text-center">
        <p className="text-gray-500">Registro no encontrado</p>
        <Button variant="link" onClick={() => navigate('/cobrettis')}>
          Volver al listado
        </Button>
      </div>
    );
  }

  const isEstadoTerminal = ['COBRADO', 'FINALIZADO', 'CANCELADO'].includes(cobretti.estado);
  const rondaActiva = rondas.find(r => r.resultado === 'EN_CURSO');

  return (
    <div className="p-6 space-y-6" data-testid="cobretti-detalle">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/cobrettis')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Cobrettis
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              <a href={`/expedientes/${cobretti.expediente_id}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                {cobretti.expediente_numero}
              </a>
            </h1>
            <p className="text-gray-500">{cobretti.contacto_deudor_nombre}</p>
          </div>
          <Badge className={ESTADO_COLORS[cobretti.estado]}>
            {cobretti.estado_display}
          </Badge>
        </div>
        
        <div className="flex gap-2">
          {/* Botón Exportar PDF */}
          <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
            <FileDown className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
          
          {/* Botón Ver Expediente */}
          <Button variant="outline" onClick={() => navigate(`/expedientes/${cobretti.expediente_id}`)} data-testid="ver-expediente-btn">
            <ExternalLink className="h-4 w-4 mr-2" />
            Ver Expediente
          </Button>
          
          {!isEstadoTerminal && (
            <>
              <Button variant="outline" onClick={handleAbrirEditar}>
                <Edit className="h-4 w-4 mr-2" />
                Editar
              </Button>
              <Button variant="outline" onClick={() => setShowEmailModal(true)}>
                <Mail className="h-4 w-4 mr-2" />
                Enviar Email
              </Button>
              <Button variant="outline" onClick={() => setShowContactoModal(true)}>
                <Phone className="h-4 w-4 mr-2" />
                Registrar Contacto
              </Button>
              <Button variant="outline" onClick={() => setShowPagoModal(true)}>
                <DollarSign className="h-4 w-4 mr-2" />
                Registrar Pago
              </Button>
              <Button onClick={() => { setEstadoForm({ nuevo_estado: cobretti.estado, motivo_cancelacion: '' }); setShowEstadoModal(true); }}>
                Cambiar Estado
              </Button>
            </>
          )}
          {(cobretti.estado === 'COBRADO' || cobretti.estado === 'FINALIZADO') && !cobretti.actuacion_id && (
            <Button onClick={handleCrearActuacion}>
              Crear Actuación
            </Button>
          )}
        </div>
      </div>

      {/* Resumen financiero */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Importe Total</p>
            <p className="text-2xl font-bold">{formatCurrency(cobretti.importe_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50">
          <CardContent className="p-4">
            <p className="text-sm text-green-600">Cobrado</p>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(cobretti.importe_cobrado)}</p>
          </CardContent>
        </Card>
        <Card className="bg-red-50">
          <CardContent className="p-4">
            <p className="text-sm text-red-600">Pendiente</p>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(cobretti.importe_pendiente)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Ronda Actual</p>
            <p className="text-2xl font-bold">{cobretti.ronda_actual}</p>
            {rondaActiva && (
              <p className="text-xs text-gray-500">
                {rondaActiva.contactos_realizados}/3 contactos
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Panel de Workflow Visual - Flujo completo del proceso */}
      <WorkflowVisual
        cobretti={cobretti}
        contactos={contactos}
        onAccion={(accion) => {
          const tipo = typeof accion === 'object' ? accion.tipo : accion;
          const valor = typeof accion === 'object' ? accion.valor : undefined;
          
          if (tipo === 'email') {
            // Cargar plantilla y abrir modal
            if (valor) handleCargarPlantilla(valor);
            setShowEmailModal(true);
          } else if (tipo === 'estado') {
            setEstadoForm({ nuevo_estado: valor, motivo_cancelacion: '' });
            setShowEstadoModal(true);
          } else if (tipo === 'telefono') {
            setContactoForm(prev => ({ ...prev, tipo_contacto: 'TELEFONO' }));
            setShowContactoModal(true);
          } else if (tipo === 'whatsapp') {
            setShowWhatsAppModal(true);
          } else if (tipo === 'tramitar') {
            // Usar modal específico para DEMANDA_EJECUCION
            if (cobretti?.estado === 'DEMANDA_EJECUCION') {
              handleAbrirDemanda();
            } else {
              setShowTramitarModal(true);
            }
          } else if (tipo === 'registrar_pago') {
            // Abrir modal de pago para estado COBRADO
            setShowPagoModal(true);
          } else if (tipo === 'finalizar_expediente') {
            // Finalizar el expediente COBRETTIS
            handleFinalizarExpediente();
          }
        }}
      />

      {/* Tabs de contenido */}
      <Tabs defaultValue="timeline" className="w-full">
        <TabsList>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
          <TabsTrigger value="rondas">Rondas ({rondas.length})</TabsTrigger>
          <TabsTrigger value="contactos">Contactos ({contactos.length})</TabsTrigger>
          <TabsTrigger value="pagos">Pagos ({pagos.length})</TabsTrigger>
          <TabsTrigger value="plan">Plan de Pagos ({planPagos.length})</TabsTrigger>
          <TabsTrigger value="info">Información</TabsTrigger>
        </TabsList>

        {/* Timeline */}
        <TabsContent value="timeline">
          <Card>
            <CardContent className="p-6">
              {timeline.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin actividad registrada</p>
              ) : (
                <div className="space-y-4">
                  {timeline.map((item, index) => (
                    <div key={index} className="flex gap-4 border-l-2 border-gray-200 pl-4 pb-4">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center -ml-9">
                        {item.tipo === 'CONTACTO' && <Phone className="h-4 w-4 text-blue-600" />}
                        {item.tipo === 'PAGO' && <DollarSign className="h-4 w-4 text-green-600" />}
                        {item.tipo === 'RONDA' && <RefreshCw className="h-4 w-4 text-orange-600" />}
                        {item.tipo === 'CREACION' && <Plus className="h-4 w-4 text-gray-600" />}
                        {item.tipo === 'CIERRE' && <CheckCircle2 className="h-4 w-4 text-green-600" />}
                      </div>
                      <div className="flex-1">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium">{item.titulo}</p>
                            <p className="text-sm text-gray-500">{item.descripcion}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{formatDateTime(item.fecha)}</span>
                            {item.tipo === 'CONTACTO' && item.contacto_id && (
                              <>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  onClick={() => {
                                    const contacto = contactos.find(c => c.id === item.contacto_id);
                                    if (contacto) handleVerDetalleContacto(contacto);
                                  }}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleEliminarContacto(item.contacto_id)}
                                  title="Eliminar"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                        {/* Mostrar adjuntos si el item tiene contacto con adjuntos */}
                        {item.tipo === 'CONTACTO' && item.contacto_id && (() => {
                          const contacto = contactos.find(c => c.id === item.contacto_id);
                          if (contacto?.adjuntos && contacto.adjuntos.length > 0) {
                            return (
                              <div className="mt-2 space-y-1">
{contacto.adjuntos.map((adj, adjIdx) => (
                                    <div key={adjIdx} className="flex items-center gap-2 text-sm">
                                      <Paperclip className="h-3 w-3 text-gray-400" />
                                      <button
                                        onClick={() => handleDescargarAdjunto(adj)}
                                        className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0"
                                      >
                                        {adj.filename || adj.nombre}
                                      </button>
                                      {adj.size && <span className="text-xs text-gray-400">({Math.round(adj.size / 1024)} KB)</span>}
                                    </div>
                                  ))}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Rondas */}
        <TabsContent value="rondas">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Rondas de Contacto</CardTitle>
              {!isEstadoTerminal && rondaActiva?.contactos_realizados >= 3 && (
                <Button size="sm" onClick={handleNuevaRonda}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Ronda
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {rondas.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin rondas registradas</p>
              ) : (
                <div className="space-y-4">
                  {rondas.map((ronda) => (
                    <div key={ronda.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-medium">Ronda {ronda.numero_ronda}</h4>
                          <p className="text-sm text-gray-500">
                            Inicio: {formatDate(ronda.fecha_inicio)}
                            {ronda.fecha_fin && ` - Fin: ${formatDate(ronda.fecha_fin)}`}
                          </p>
                        </div>
                        <div className="text-right">
                          <Badge variant={ronda.resultado === 'EN_CURSO' ? 'default' : 'secondary'}>
                            {ronda.resultado}
                          </Badge>
                          <p className="text-sm text-gray-500 mt-1">
                            {ronda.contactos_realizados}/3 contactos
                          </p>
                        </div>
                      </div>
                      {ronda.notas && (
                        <p className="text-sm text-gray-600 mt-2 bg-gray-50 p-2 rounded">{ronda.notas}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Contactos */}
        <TabsContent value="contactos">
          <Card>
            <CardContent className="p-0">
              {contactos.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin contactos registrados</p>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fecha</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Tipo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Contenido</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Resultado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Registrado por</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {contactos.map((contacto) => {
                      const Icon = TIPO_CONTACTO_ICONS[contacto.tipo_contacto] || FileText;
                      return (
                        <tr key={contacto.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{formatDateTime(contacto.fecha_contacto)}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4 text-gray-400" />
                              <span className="text-sm">{contacto.tipo_contacto}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm max-w-xs truncate">
                            {contacto.asunto || contacto.contenido?.substring(0, 100)}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <Badge variant="outline">{contacto.resultado}</Badge>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">{contacto.registrado_por_nombre}</td>
                          <td className="px-4 py-3 text-center">
                            <Button size="sm" variant="ghost" onClick={() => handleVerDetalleContacto(contacto)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pagos */}
        <TabsContent value="pagos">
          <Card>
            <CardContent className="p-0">
              {pagos.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin pagos registrados</p>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Fecha</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Importe</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Método</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Referencia</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Registrado por</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {pagos.map((pago) => (
                      <tr key={pago.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm">{formatDate(pago.fecha_pago)}</td>
                        <td className="px-4 py-3 text-right font-medium text-green-600">
                          {formatCurrency(pago.importe)}
                        </td>
                        <td className="px-4 py-3 text-sm">{pago.metodo_pago}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{pago.referencia || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{pago.registrado_por_nombre}</td>
                        <td className="px-4 py-3 text-center">
                          {!isEstadoTerminal && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEliminarPago(pago.id)}
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Plan de Pagos */}
        <TabsContent value="plan">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Plan de Pagos</CardTitle>
              {!isEstadoTerminal && planPagos.length === 0 && (
                <Button size="sm" onClick={() => setShowPlanPagosModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Crear Plan
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {planPagos.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Sin plan de pagos</p>
              ) : (
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Cuota</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500">Importe</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Vencimiento</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Estado</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-gray-500">Fecha Pago</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {planPagos.map((cuota) => (
                      <tr key={cuota.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium">#{cuota.numero_cuota}</td>
                        <td className="px-4 py-3 text-right font-medium">{formatCurrency(cuota.importe_cuota)}</td>
                        <td className="px-4 py-3 text-center text-sm">{formatDate(cuota.fecha_vencimiento)}</td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant={cuota.estado === 'PAGADA' ? 'success' : cuota.estado === 'IMPAGADA' ? 'destructive' : 'outline'}>
                            {cuota.estado}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-gray-500">
                          {cuota.fecha_pago_real ? formatDate(cuota.fecha_pago_real) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Información */}
        <TabsContent value="info">
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Tipo de deuda</p>
                  <p className="font-medium">{cobretti.tipo_deuda}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Fecha creación</p>
                  <p className="font-medium">{formatDateTime(cobretti.fecha_creacion)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Último contacto</p>
                  <p className="font-medium">{formatDateTime(cobretti.fecha_ultimo_contacto) || 'Sin contactos'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Creado por</p>
                  <p className="font-medium">{cobretti.creado_por_nombre}</p>
                </div>
              </div>
              
              <div>
                <p className="text-sm text-gray-500">Concepto</p>
                <p className="font-medium">{cobretti.concepto}</p>
              </div>
              
              {cobretti.notas && (
                <div>
                  <p className="text-sm text-gray-500">Notas</p>
                  <p className="bg-gray-50 p-3 rounded">{cobretti.notas}</p>
                </div>
              )}
              
              {cobretti.motivo_cancelacion && (
                <div>
                  <p className="text-sm text-gray-500">Motivo de cancelación</p>
                  <p className="bg-red-50 p-3 rounded text-red-800">{cobretti.motivo_cancelacion}</p>
                </div>
              )}
              
              <div className="pt-4 border-t">
                <p className="text-sm text-gray-500 mb-2">Desglose de importes</p>
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-gray-50 p-3 rounded text-center">
                    <p className="text-xs text-gray-500">Principal</p>
                    <p className="font-bold">{formatCurrency(cobretti.importe_principal)}</p>
                  </div>
                  <div className="bg-gray-50 p-3 rounded text-center">
                    <p className="text-xs text-gray-500">Intereses</p>
                    <p className="font-bold">{formatCurrency(cobretti.importe_intereses)}</p>
                  </div>
                  <div className="bg-blue-50 p-3 rounded text-center">
                    <p className="text-xs text-blue-600">Total</p>
                    <p className="font-bold text-blue-600">{formatCurrency(cobretti.importe_total)}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal: Registrar Contacto */}
      <Dialog open={showContactoModal} onOpenChange={setShowContactoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Contacto</DialogTitle>
            <DialogDescription>
              Registre un intento de contacto con el deudor
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Contacto</Label>
              <Select
                value={contactoForm.tipo_contacto}
                onValueChange={(v) => setContactoForm(p => ({ ...p, tipo_contacto: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TELEFONO">Teléfono</SelectItem>
                  <SelectItem value="EMAIL">Email</SelectItem>
                  <SelectItem value="BUROFAX">Burofax</SelectItem>
                  <SelectItem value="CARTA">Carta</SelectItem>
                  <SelectItem value="PRESENCIAL">Presencial</SelectItem>
                  <SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Asunto</Label>
              <Input
                value={contactoForm.asunto}
                onChange={(e) => setContactoForm(p => ({ ...p, asunto: e.target.value }))}
                placeholder="Asunto del contacto..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contenido / Descripción *</Label>
              <Textarea
                value={contactoForm.contenido}
                onChange={(e) => setContactoForm(p => ({ ...p, contenido: e.target.value }))}
                placeholder="Describa el contacto realizado..."
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Resultado</Label>
              <Select
                value={contactoForm.resultado}
                onValueChange={(v) => setContactoForm(p => ({ ...p, resultado: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENVIADO">Enviado</SelectItem>
                  <SelectItem value="ENTREGADO">Entregado</SelectItem>
                  <SelectItem value="NO_CONTESTA">No contesta</SelectItem>
                  <SelectItem value="CONTESTADO">Contestado</SelectItem>
                  <SelectItem value="RECHAZADO">Rechazado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Respuesta del deudor (si aplica)</Label>
              <Textarea
                value={contactoForm.respuesta_deudor}
                onChange={(e) => setContactoForm(p => ({ ...p, respuesta_deudor: e.target.value }))}
                placeholder="Respuesta recibida..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactoModal(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarContacto}>Registrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Registrar Pago */}
      <Dialog open={showPagoModal} onOpenChange={setShowPagoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registrar Pago</DialogTitle>
            <DialogDescription>
              Pendiente: {formatCurrency(cobretti.importe_pendiente)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Fecha del Pago</Label>
              <Input
                type="date"
                value={pagoForm.fecha_pago}
                onChange={(e) => setPagoForm(p => ({ ...p, fecha_pago: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Importe (€) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                max={cobretti.importe_pendiente}
                value={pagoForm.importe}
                onChange={(e) => setPagoForm(p => ({ ...p, importe: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Método de Pago</Label>
              <Select
                value={pagoForm.metodo_pago}
                onValueChange={(v) => setPagoForm(p => ({ ...p, metodo_pago: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TRANSFERENCIA">Transferencia</SelectItem>
                  <SelectItem value="EFECTIVO">Efectivo</SelectItem>
                  <SelectItem value="CHEQUE">Cheque</SelectItem>
                  <SelectItem value="EMBARGO">Embargo</SelectItem>
                  <SelectItem value="OTRO">Otro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Referencia</Label>
              <Input
                value={pagoForm.referencia}
                onChange={(e) => setPagoForm(p => ({ ...p, referencia: e.target.value }))}
                placeholder="Nº de transferencia, recibo, etc."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Notas</Label>
              <Textarea
                value={pagoForm.notas}
                onChange={(e) => setPagoForm(p => ({ ...p, notas: e.target.value }))}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPagoModal(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarPago}>Registrar Pago</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Cambiar Estado */}
      <Dialog open={showEstadoModal} onOpenChange={setShowEstadoModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cambiar Estado</DialogTitle>
            <DialogDescription>
              Estado actual: {cobretti.estado_display}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nuevo Estado</Label>
              <Select
                value={estadoForm.nuevo_estado}
                onValueChange={(v) => setEstadoForm(p => ({ ...p, nuevo_estado: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione estado" />
                </SelectTrigger>
                <SelectContent>
                  {estados.map(e => (
                    <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {estadoForm.nuevo_estado === 'CANCELADO' && (
              <div className="space-y-2">
                <Label>Motivo de Cancelación *</Label>
                <Textarea
                  value={estadoForm.motivo_cancelacion}
                  onChange={(e) => setEstadoForm(p => ({ ...p, motivo_cancelacion: e.target.value }))}
                  placeholder="Explique el motivo de la cancelación..."
                  rows={3}
                />
              </div>
            )}
            
            {['EXTRAJUDICIAL', 'DEMANDA_EJECUCION'].includes(estadoForm.nuevo_estado) && (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm text-yellow-800">
                <AlertTriangle className="h-4 w-4 inline mr-2" />
                Se creará automáticamente una tarea para el seguimiento.
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEstadoModal(false)}>Cancelar</Button>
            <Button onClick={handleCambiarEstado}>Cambiar Estado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Enviar Email */}
      <Dialog open={showEmailModal} onOpenChange={setShowEmailModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Enviar Email al Deudor</DialogTitle>
            <DialogDescription>
              Destinatario: {cobretti.contacto_deudor_nombre} ({cobretti.contacto_deudor_email || 'Sin email'})
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Plantilla (opcional)</Label>
                <Select
                  value={emailForm.plantilla_id || "NONE"}
                  onValueChange={(v) => {
                    if (v && v !== "NONE") handleCargarPlantilla(v);
                    else setEmailForm(p => ({ ...p, plantilla_id: '' }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione plantilla" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Sin plantilla</SelectItem>
                    {plantillas.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Cuenta de Email *</Label>
                <Select
                  value={emailForm.cuenta_smtp_id || "NONE"}
                  onValueChange={(v) => setEmailForm(p => ({ ...p, cuenta_smtp_id: v === "NONE" ? "" : v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccione cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NONE">Seleccione cuenta</SelectItem>
                    {cuentasEmail.map(c => (
                      <SelectItem key={c.id} value={String(c.id)}>{c.email}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Email Destinatario *</Label>
              <Input
                type="email"
                value={emailForm.destinatario_email}
                onChange={(e) => setEmailForm(p => ({ ...p, destinatario_email: e.target.value }))}
                placeholder="email@ejemplo.com"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Asunto *</Label>
              <Input
                value={emailForm.asunto}
                onChange={(e) => setEmailForm(p => ({ ...p, asunto: e.target.value }))}
                placeholder="Asunto del email..."
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contenido *</Label>
              <Textarea
                value={emailForm.contenido}
                onChange={(e) => setEmailForm(p => ({ ...p, contenido: e.target.value }))}
                placeholder="Redacte el contenido del email..."
                rows={10}
                className="text-sm"
              />
              <p className="text-xs text-gray-500">
                Puede usar formato: saltos de línea, tabulaciones, etc. El contenido se enviará tal como lo escriba.
              </p>
            </div>
            
            {/* Archivos adjuntos */}
            <div className="space-y-2">
              <Label>Archivos adjuntos</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  ref={emailFileInputRef}
                  onChange={handleEmailFileSelect}
                  multiple
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-gray-400" />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => emailFileInputRef.current?.click()}
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    Adjuntar archivos
                  </Button>
                  <p className="text-xs text-gray-500">Facturas, contratos, documentos de deuda, etc.</p>
                </div>
              </div>
              
              {emailAdjuntos.length > 0 && (
                <div className="space-y-2 mt-2">
                  {emailAdjuntos.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <Paperclip className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeEmailAdjunto(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => { setShowEmailModal(false); setEmailAdjuntos([]); }}>Cancelar</Button>
            <Button type="button" onClick={handleEnviarEmail}>
              <Send className="h-4 w-4 mr-2" />
              Enviar Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Generar Plan de Pagos */}
      <Dialog open={showPlanPagosModal} onOpenChange={setShowPlanPagosModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Generar Plan de Pagos</DialogTitle>
            <DialogDescription>
              Importe pendiente: {formatCurrency(cobretti.importe_pendiente)}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número de Cuotas</Label>
              <Input
                type="number"
                min="1"
                max="60"
                value={planAutoForm.num_cuotas}
                onChange={(e) => setPlanAutoForm(p => ({ ...p, num_cuotas: e.target.value }))}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Fecha Primera Cuota</Label>
              <Input
                type="date"
                value={planAutoForm.fecha_primera_cuota}
                onChange={(e) => setPlanAutoForm(p => ({ ...p, fecha_primera_cuota: e.target.value }))}
              />
            </div>
            
            {planAutoForm.num_cuotas > 0 && cobretti.importe_pendiente > 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded">
                <div className="text-sm text-blue-800">
                  <p><strong>Cuota mensual aproximada:</strong></p>
                  <p className="text-2xl font-bold">
                    {formatCurrency(cobretti.importe_pendiente / parseInt(planAutoForm.num_cuotas || 1))}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPlanPagosModal(false)}>Cancelar</Button>
            <Button onClick={handleGenerarPlanPagos}>
              <Calendar className="h-4 w-4 mr-2" />
              Generar Plan
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Editar Registro */}
      <Dialog open={showEditarModal} onOpenChange={setShowEditarModal}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Editar Registro de Cobro</DialogTitle>
            <DialogDescription>
              Modifique los datos del registro. El importe total se recalculará automáticamente.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Tipo de Deuda</Label>
              <Select
                value={editarForm.tipo_deuda}
                onValueChange={(v) => setEditarForm(p => ({ ...p, tipo_deuda: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccione tipo" />
                </SelectTrigger>
                <SelectContent>
                  {tiposDeuda.map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Concepto *</Label>
              <Textarea
                value={editarForm.concepto}
                onChange={(e) => setEditarForm(p => ({ ...p, concepto: e.target.value }))}
                placeholder="Descripción del concepto de la deuda..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Importe Principal (€) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editarForm.importe_principal}
                  onChange={(e) => setEditarForm(p => ({ ...p, importe_principal: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
              <div className="space-y-2">
                <Label>Importe Intereses (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editarForm.importe_intereses}
                  onChange={(e) => setEditarForm(p => ({ ...p, importe_intereses: e.target.value }))}
                  placeholder="0.00"
                />
              </div>
            </div>
            
            {(editarForm.importe_principal || editarForm.importe_intereses) && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-blue-800">Nuevo Importe Total:</span>
                  <span className="text-xl font-bold text-blue-600">
                    {formatCurrency(
                      (parseFloat(editarForm.importe_principal) || 0) + 
                      (parseFloat(editarForm.importe_intereses) || 0)
                    )}
                  </span>
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                value={editarForm.notas}
                onChange={(e) => setEditarForm(p => ({ ...p, notas: e.target.value }))}
                placeholder="Notas adicionales..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditarModal(false)}>Cancelar</Button>
            <Button onClick={handleGuardarEdicion}>
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: WhatsApp */}
      <Dialog open={showWhatsAppModal} onOpenChange={setShowWhatsAppModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Registrar Contacto por WhatsApp</DialogTitle>
            <DialogDescription>
              Registre los mensajes enviados al deudor por WhatsApp
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Mensajes enviados *</Label>
              <Textarea
                value={whatsAppForm.contenido}
                onChange={(e) => setWhatsAppForm(p => ({ ...p, contenido: e.target.value }))}
                placeholder="Describa los mensajes que ha enviado al cliente por WhatsApp...&#10;&#10;Ejemplo:&#10;- 10:30: Hola, le escribo del despacho...&#10;- 10:35: Adjunto factura pendiente...&#10;- 11:00: ¿Ha podido revisar el mensaje?"
                rows={6}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Resultado del contacto</Label>
              <Select
                value={whatsAppForm.resultado}
                onValueChange={(v) => setWhatsAppForm(p => ({ ...p, resultado: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ENVIADO">Enviado (sin respuesta aún)</SelectItem>
                  <SelectItem value="CONTESTADO">Contestado</SelectItem>
                  <SelectItem value="LEIDO">Leído (sin respuesta)</SelectItem>
                  <SelectItem value="NO_ENTREGADO">No entregado</SelectItem>
                  <SelectItem value="BLOQUEADO">Bloqueado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Respuesta del deudor (si aplica)</Label>
              <Textarea
                value={whatsAppForm.respuesta_deudor}
                onChange={(e) => setWhatsAppForm(p => ({ ...p, respuesta_deudor: e.target.value }))}
                placeholder="¿Qué respondió el deudor?..."
                rows={3}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWhatsAppModal(false)}>Cancelar</Button>
            <Button onClick={handleRegistrarWhatsApp} className="bg-green-600 hover:bg-green-700">
              <MessageCircle className="h-4 w-4 mr-2" />
              Registrar WhatsApp
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Tramitar Fase */}
      <Dialog open={showTramitarModal} onOpenChange={setShowTramitarModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Tramitar Fase: {cobretti?.estado === 'EXTRAJUDICIAL' ? 'Extrajudicial' : 'Demanda Ejecución'}</DialogTitle>
            <DialogDescription>
              Registre las acciones realizadas y adjunte los documentos generados
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Descripción de las acciones realizadas *</Label>
              <Textarea
                value={tramitarForm.descripcion}
                onChange={(e) => setTramitarForm(p => ({ ...p, descripcion: e.target.value }))}
                placeholder={cobretti?.estado === 'EXTRAJUDICIAL' 
                  ? "Describa el envío del burofax, carta certificada, etc..."
                  : "Describa las actuaciones judiciales realizadas..."
                }
                rows={5}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Resultado</Label>
              <Select
                value={tramitarForm.resultado}
                onValueChange={(v) => setTramitarForm(p => ({ ...p, resultado: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="COMPLETADO">Completado</SelectItem>
                  <SelectItem value="EN_PROCESO">En proceso</SelectItem>
                  <SelectItem value="PENDIENTE_RESPUESTA">Pendiente de respuesta</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Archivos adjuntos */}
            <div className="space-y-2">
              <Label>Documentos adjuntos</Label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                <input
                  type="file"
                  ref={tramitarFileInputRef}
                  onChange={handleTramitarFileSelect}
                  multiple
                  className="hidden"
                />
                <div className="flex flex-col items-center gap-2">
                  <Upload className="h-8 w-8 text-gray-400" />
                  <Button 
                    type="button"
                    variant="outline"
                    onClick={() => tramitarFileInputRef.current?.click()}
                  >
                    <Paperclip className="h-4 w-4 mr-2" />
                    Seleccionar archivos
                  </Button>
                  <p className="text-xs text-gray-500">Adjunte burofax, acuses de recibo, documentos judiciales, etc.</p>
                </div>
              </div>
              
              {tramitarAdjuntos.length > 0 && (
                <div className="space-y-2 mt-2">
                  {tramitarAdjuntos.map((file, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-gray-400" />
                        <span className="text-sm">{file.name}</span>
                        <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                      </div>
                      <Button size="sm" variant="ghost" onClick={() => removeTramitarAdjunto(index)}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTramitarModal(false)}>Cancelar</Button>
            <Button onClick={handleTramitarFase} className="bg-purple-600 hover:bg-purple-700">
              <CheckSquare className="h-4 w-4 mr-2" />
              Tramitar Fase
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal: Ver Detalle de Contacto */}
      <Dialog open={showDetalleContactoModal} onOpenChange={setShowDetalleContactoModal}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Detalle del Contacto - {contactoDetalle?.tipo_contacto}
            </DialogTitle>
            <DialogDescription>
              Registrado el {contactoDetalle && formatDateTime(contactoDetalle.fecha_contacto)} por {contactoDetalle?.registrado_por_nombre}
            </DialogDescription>
          </DialogHeader>
          
          {contactoDetalle && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Tipo de Contacto</Label>
                  <p className="font-medium flex items-center gap-2">
                    {contactoDetalle.tipo_contacto === 'EMAIL' && <Mail className="h-4 w-4" />}
                    {contactoDetalle.tipo_contacto === 'TELEFONO' && <Phone className="h-4 w-4" />}
                    {contactoDetalle.tipo_contacto === 'WHATSAPP' && <MessageCircle className="h-4 w-4" />}
                    {contactoDetalle.tipo_contacto === 'BUROFAX' && <FileText className="h-4 w-4" />}
                    {contactoDetalle.tipo_contacto}
                  </p>
                </div>
                <div>
                  <Label className="text-gray-500">Resultado</Label>
                  <Badge variant="outline">{contactoDetalle.resultado}</Badge>
                </div>
              </div>
              
              {contactoDetalle.destinatario_email && (
                <div>
                  <Label className="text-gray-500">Destinatario</Label>
                  <p>{contactoDetalle.destinatario_email}</p>
                </div>
              )}
              
              {contactoDetalle.asunto && (
                <div>
                  <Label className="text-gray-500">Asunto</Label>
                  <p className="font-medium">{contactoDetalle.asunto}</p>
                </div>
              )}
              
              <div>
                <Label className="text-gray-500">Contenido / Mensaje</Label>
                <div className="mt-2 p-4 bg-gray-50 rounded-lg border whitespace-pre-wrap text-sm">
                  {contactoDetalle.contenido || 'Sin contenido'}
                </div>
              </div>
              
              {contactoDetalle.respuesta_deudor && (
                <div>
                  <Label className="text-gray-500">Respuesta del Deudor</Label>
                  <div className="mt-2 p-4 bg-yellow-50 rounded-lg border border-yellow-200 whitespace-pre-wrap text-sm">
                    {contactoDetalle.respuesta_deudor}
                  </div>
                </div>
              )}
              
              {contactoDetalle.adjuntos && contactoDetalle.adjuntos.length > 0 && (
                <div>
                  <Label className="text-gray-500">Archivos Adjuntos</Label>
                  <div className="space-y-2 mt-2">
{contactoDetalle.adjuntos.map((adj, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded border">
                          <div className="flex items-center gap-2">
                            <Paperclip className="h-4 w-4 text-gray-400" />
                            <span className="text-sm">{adj.nombre || adj.filename}</span>
                            {adj.size && <span className="text-xs text-gray-400">({Math.round(adj.size / 1024)} KB)</span>}
                          </div>
                          <Button size="sm" variant="outline" onClick={() => handleDescargarAdjunto(adj)}>
                            <Download className="h-4 w-4 mr-1" />
                            Descargar
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button onClick={() => setShowDetalleContactoModal(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================== */}
      {/* Modal: Demanda de Ejecución (COMPLEJO) */}
      {/* ========================================== */}
      <Dialog open={showDemandaModal} onOpenChange={(open) => {
        if (!open && !demandaLoading) setShowDemandaModal(false);
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Scale className="h-5 w-5 text-purple-600" />
              Tramitar Demanda de Ejecución
            </DialogTitle>
            <DialogDescription>
              {!demandaCompletada 
                ? 'Configure los parámetros de la demanda judicial y el envío al procurador'
                : 'Demanda tramitada correctamente. Puede avanzar al estado de Cobrado.'}
            </DialogDescription>
          </DialogHeader>
          
          {demandaLoading && !demandaCompletada && (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin text-purple-600" />
              <span className="ml-2">Cargando datos...</span>
            </div>
          )}
          
          {!demandaLoading && !demandaCompletada && datosDemanda && (
            <div className="space-y-6 py-4">
              {/* Resumen del caso */}
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-800 mb-2">Resumen del Caso</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Expediente:</span>
                    <p className="font-medium">{datosDemanda.expediente_original?.numero_expediente || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Deudor:</span>
                    <p className="font-medium">{datosDemanda.deudor?.nombre || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">DNI/NIF:</span>
                    <p className="font-medium">{datosDemanda.deudor?.nif_cif || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-gray-500">Importe pendiente:</span>
                    <p className="font-bold text-red-600">{formatCurrency(datosDemanda.cobretti?.importe_pendiente)}</p>
                  </div>
                </div>
              </div>
              
              {/* Selector de resultado */}
              <div className="space-y-2">
                <Label className="text-base font-semibold">Estado de la tramitación *</Label>
                <Select
                  value={demandaForm.resultado}
                  onValueChange={(v) => setDemandaForm(p => ({ ...p, resultado: v }))}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="EN_PROCESO">En proceso (guardar progreso)</SelectItem>
                    <SelectItem value="PENDIENTE_RESPUESTA">Pendiente de respuesta</SelectItem>
                    <SelectItem value="COMPLETADO">Completado (crear expediente y enviar al procurador)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {demandaForm.resultado === 'COMPLETADO' 
                    ? 'Se creará un nuevo expediente y se enviará el email al procurador'
                    : 'Guarda el progreso sin crear el expediente ni enviar email'}
                </p>
              </div>
              
              {/* Descripción (para EN_PROCESO y PENDIENTE_RESPUESTA) */}
              {demandaForm.resultado !== 'COMPLETADO' && (
                <div className="space-y-2">
                  <Label>Descripción de las acciones realizadas *</Label>
                  <Textarea
                    value={demandaForm.descripcion}
                    onChange={(e) => setDemandaForm(p => ({ ...p, descripcion: e.target.value }))}
                    placeholder="Describa las actuaciones judiciales realizadas..."
                    rows={4}
                  />
                </div>
              )}
              
              {/* SECCIÓN COMPLETADO: Configuración de email al procurador */}
              {demandaForm.resultado === 'COMPLETADO' && (
                <div className="space-y-6 border-t pt-6">
                  <div className="flex items-center gap-2 text-lg font-semibold text-purple-800">
                    <Send className="h-5 w-5" />
                    Preparar email al Procurador
                  </div>
                  
                  {/* Cuenta SMTP */}
                  <div className="space-y-2">
                    <Label>Cuenta de email para envío *</Label>
                    <Select
                      value={demandaForm.cuenta_smtp_id}
                      onValueChange={(v) => setDemandaForm(p => ({ ...p, cuenta_smtp_id: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccione cuenta de email" />
                      </SelectTrigger>
                      <SelectContent>
                        {datosDemanda.cuentas_email?.map(cuenta => (
                          <SelectItem key={cuenta.id} value={cuenta.id.toString()}>
                            {cuenta.nombre || cuenta.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Procurador */}
                  <div className="space-y-3">
                    <Label>Procurador destinatario *</Label>
                    
                    {datosDemanda.procurador && (
                      <div className="p-3 bg-gray-50 rounded-lg border flex items-center justify-between">
                        <div>
                          <p className="font-medium">{datosDemanda.procurador.nombre}</p>
                          <p className="text-sm text-gray-500">{datosDemanda.procurador.email}</p>
                        </div>
                        <Badge variant="outline" className="bg-green-50 text-green-700 border-green-300">
                          Procurador del expediente
                        </Badge>
                      </div>
                    )}
                    
                    {!datosDemanda.procurador && (
                      <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-300">
                        <p className="text-sm text-yellow-800">
                          No hay procurador asignado al expediente original. Seleccione uno de la lista.
                        </p>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        id="usar_otro_procurador"
                        checked={demandaForm.usar_otro_procurador}
                        onChange={(e) => setDemandaForm(p => ({ ...p, usar_otro_procurador: e.target.checked }))}
                        className="h-4 w-4"
                      />
                      <label htmlFor="usar_otro_procurador" className="text-sm">
                        Usar otro procurador diferente
                      </label>
                    </div>
                    
                    {(demandaForm.usar_otro_procurador || !datosDemanda.procurador) && (
                      <Select
                        value={demandaForm.usar_otro_procurador ? demandaForm.nuevo_procurador_id : demandaForm.procurador_id}
                        onValueChange={(v) => {
                          if (demandaForm.usar_otro_procurador) {
                            setDemandaForm(p => ({ ...p, nuevo_procurador_id: v }));
                          } else {
                            setDemandaForm(p => ({ ...p, procurador_id: v }));
                          }
                        }}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccione procurador" />
                        </SelectTrigger>
                        <SelectContent>
                          {datosDemanda.lista_procuradores?.map(proc => (
                            <SelectItem key={proc.id} value={proc.id.toString()}>
                              {proc.nombre} - {proc.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                  
                  {/* Asunto del email */}
                  <div className="space-y-2">
                    <Label>Asunto del email *</Label>
                    <Input
                      value={demandaForm.asunto_email}
                      onChange={(e) => setDemandaForm(p => ({ ...p, asunto_email: e.target.value }))}
                      placeholder="Asunto del email al procurador"
                    />
                    <p className="text-xs text-gray-500">
                      "NUEVO_EXP" se reemplazará automáticamente por el número del nuevo expediente
                    </p>
                  </div>
                  
                  {/* Contenido del email */}
                  <div className="space-y-2">
                    <Label>Contenido del email *</Label>
                    <Textarea
                      value={demandaForm.contenido_email}
                      onChange={(e) => setDemandaForm(p => ({ ...p, contenido_email: e.target.value }))}
                      rows={12}
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-gray-500">
                      Puede editar el texto. "NUEVO_EXP" se reemplazará por el número del expediente
                    </p>
                  </div>
                  
                  {/* Archivos adjuntos */}
                  <div className="space-y-2">
                    <Label>Documentos a adjuntar</Label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        ref={demandaFileInputRef}
                        onChange={handleDemandaFileSelect}
                        multiple
                        className="hidden"
                      />
                      <div className="flex flex-col items-center gap-2">
                        <Upload className="h-8 w-8 text-gray-400" />
                        <Button 
                          type="button"
                          variant="outline"
                          onClick={() => demandaFileInputRef.current?.click()}
                        >
                          <Paperclip className="h-4 w-4 mr-2" />
                          Adjuntar documentos
                        </Button>
                        <p className="text-xs text-gray-500">Demanda, documentación, escritos judiciales...</p>
                      </div>
                    </div>
                    
                    {demandaAdjuntos.length > 0 && (
                      <div className="space-y-2 mt-2">
                        {demandaAdjuntos.map((file, index) => (
                          <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-400" />
                              <span className="text-sm">{file.name}</span>
                              <span className="text-xs text-gray-400">({(file.size / 1024).toFixed(1)} KB)</span>
                            </div>
                            <Button size="sm" variant="ghost" onClick={() => removeDemandaAdjunto(index)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  
                  {/* Checkbox enviar email */}
                  <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <input 
                      type="checkbox" 
                      id="enviar_email"
                      checked={demandaForm.enviar_email}
                      onChange={(e) => setDemandaForm(p => ({ ...p, enviar_email: e.target.checked }))}
                      className="h-4 w-4"
                    />
                    <label htmlFor="enviar_email" className="text-sm">
                      Enviar email automáticamente al procurador
                    </label>
                    <span className="text-xs text-gray-500 ml-auto">
                      (Desmarque si prefiere enviar manualmente)
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Vista de demanda completada */}
          {demandaCompletada && expedienteDemandaCreado && (
            <div className="py-6">
              <div className="text-center p-6 bg-green-50 rounded-lg border border-green-300">
                <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
                <h3 className="text-xl font-bold text-green-800 mb-2">¡Demanda Tramitada!</h3>
                <p className="text-green-700 mb-4">
                  Se ha creado el expediente <strong>{expedienteDemandaCreado.numero}</strong>
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  {expedienteDemandaCreado.titulo}
                </p>
                
                <div className="flex flex-col gap-3">
                  <Button 
                    onClick={handleConfirmarFinalizacion}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Confirmar y avanzar a "Cobrado"
                  </Button>
                  
                  <Button 
                    variant="outline"
                    onClick={() => navigate(`/expedientes/${expedienteDemandaCreado.id}`)}
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Ver nuevo expediente
                  </Button>
                  
                  <Button 
                    variant="ghost"
                    onClick={() => setShowDemandaModal(false)}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Footer con botones */}
          {!demandaCompletada && (
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => setShowDemandaModal(false)}
                disabled={demandaLoading}
              >
                Cancelar
              </Button>
              
              {demandaForm.resultado !== 'COMPLETADO' ? (
                <Button 
                  onClick={handleGuardarProgresoDemanda}
                  disabled={demandaLoading}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Guardar Progreso
                </Button>
              ) : (
                <Button 
                  onClick={handleCompletarDemanda}
                  disabled={demandaLoading}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {demandaLoading ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Procesando...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Completar y Enviar al Procurador
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
